import React, { useEffect } from 'react'
import { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Fab } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import CookieBanner from '../components/CookieBanner/CookieBanner'
import Header from '../components/Header/Header'
import { ConciergeOverlay } from '../components/ConciergeOverlay/ConciergeOverlay'
import './syles.scss'

// The concierge bot is "configured" only when the build-time env var is
// present. When absent (e.g. older bundles, .env.production not updated),
// the FAB and overlay both hide so the layout falls back to the prior
// single-FAB look.
const conciergeEnabled = Boolean(process.env.REACT_APP_CONCIERGE_BOT_USER_ID)

// The bottom tab bar was retired. `back` shows the header back arrow (sub-screens),
// `showFab` opts a page into the bottom-right New-Post button (Home). `selected`
// is accepted-but-ignored for back-compat with existing callers.
export const AppLayout = ({ back, noHeader, showFab, fill, ...props }) => {
  const [isCookie, setIsCookie] = useState(window.localStorage.getItem('isCookieAccepted') || false)
  const [conciergeOpen, setConciergeOpen] = useState(false)
  const history = useHistory()
  const { pathname } = useLocation()
  const intl = useIntl()
  // Concierge FAB is scoped to Home only. Other surfaces (PostDetail,
  // AddPost, ChatPage, Settings, Blog) get cluttered by a second
  // floating button — and users browsing those screens are mid-task,
  // not in "ask the bot anything" mode. /chats already hosts a real
  // conversation with the bot, so a FAB there would be redundant.
  const conciergeAllowed = conciergeEnabled && pathname === '/'
  const isCookieAccepted = window.localStorage.getItem('isCookieAccepted') || false
  const navigateTo = (path, params =  {}) => {
    history.push(`/${path}`, params)
  }

  const acceptCookie = () => {
    window.localStorage.setItem('isCookieAccepted', true)
    setIsCookie(true)
  }

  useEffect(() => {
    setIsCookie(isCookieAccepted)
  }, [isCookieAccepted])

  return (
    <div style={fill ? { height: window.innerHeight } : { minHeight: window.innerHeight }} className='w-full flex flex-1 flex-col overflow-hidden'>
      {!noHeader && <Header back={back} />}
        {/* `fill` pages (e.g. Chat) own the remaining height and scroll
            internally; default pages scroll the page and pad for the FAB. */}
        <div className={fill ? 'w-full flex flex-1 flex-col z-0 overflow-hidden' : 'w-full flex flex-1 flex-col z-0 overflow-y-auto pb-20'}>
        {props.children}
        </div>

        {/* New-post floating button (opt-in, e.g. Home). Lifts above the cookie
            banner while it's showing so it never covers "I understand". */}
        {showFab && (
          <Fab
            color='primary'
            aria-label={intl.formatMessage({ id: 'appLayout.aria.addPost', defaultMessage: 'Add post' })}
            onClick={() => history.push('/add-post')}
            className={`fixed right-4 z-20 ${!isCookie ? 'bottom-24' : 'bottom-4'}`}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Concierge bot FAB — stacked above the Add-post FAB when both are
            on screen, drops down into the Add-post slot when only concierge
            shows (e.g. detail pages where there's no posting affordance).
            Always sits one shelf above wherever the Add-post FAB would be.
            Rendered only when the build embeds a bot UUID; the overlay
            below checks the same. */}
        {conciergeAllowed && (
          <Fab
            color='default'
            size='medium'
            aria-label={intl.formatMessage({ id: 'appLayout.aria.concierge', defaultMessage: 'Chat with the dresez concierge' })}
            onClick={() => setConciergeOpen(true)}
            className={`fixed right-4 z-20 bg-white dark:bg-[#2a2c30] text-[#3a6df0] shadow-md ${
              showFab
                ? (!isCookie ? 'bottom-40' : 'bottom-20')
                : (!isCookie ? 'bottom-24' : 'bottom-4')
            }`}
          >
            <SmartToyOutlinedIcon />
          </Fab>
        )}
        {conciergeAllowed && (
          <ConciergeOverlay open={conciergeOpen} onClose={() => setConciergeOpen(false)} />
        )}

        {/* Cookie banner only until accepted — no empty fixed bar afterwards. */}
        {!isCookie && (
          <div className='w-full z-10 fixed bottom-0 bg-white dark:bg-[#17181c] dark:border-t dark:border-[#2a2c30] tabBarOfBottom'>
            <CookieBanner acceptCookie={acceptCookie} navigateTo={navigateTo} />
          </div>
        )}
    </div>
  )
}
